import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import type { FeaturedToy } from '../types/toy'
import { entryToToy } from '../types/toyEntry'
import { useCurrentIo } from '../auth/useImpersonation'
import { useAdminIo } from '../auth/useAdmin'
import { loadToyIndex } from '../upload/toyIndex'
import {
  filterNewerBuilds,
  firmwareMetaOf,
  isNewerBuild,
  listFirmwareBuilds,
  parseableTime,
  sortBuildsNewestFirst,
  type FirmwareBuild,
} from '../upload/fetchFirmware'
import {
  inspectToy,
  listToyVersionSummaries,
  loadVersionMetadata,
  verifyToyShas,
  type ToyFileRow,
  type ToyInspection,
  type ToyVersionSummary,
} from '../upload/toyInspection'
import { bumpMinor, upgradeToyFirmware } from '../upload/upgradeToyFirmware'
import { promoteToyVersion } from '../upload/promoteToyVersion'
import {
  copyVersionToFeatured,
  deleteToyVersion,
  FEATURED_IDENTITY_ID,
} from '../upload/adminS3'
import { isSemver } from '../upload/toyVersions'
import { fetchFeaturedToys } from '../lib/featuredToys'
import './ToyDetail.css'
import './Admin.css'

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; toy: FeaturedToy }
  | { kind: 'not-found' }
  | { kind: 'error'; message: string }

type InspectionState =
  | { kind: 'loading' }
  | { kind: 'ready'; inspection: ToyInspection }
  | { kind: 'error'; message: string }

const PRESENCE_LABELS: Record<ToyFileRow['presence'], { label: string; cls: string }> = {
  both: { label: 'folder + manifest', cls: 'indexed' },
  'folder-only': { label: 'folder only', cls: 'orphan' },
  'manifest-only': { label: 'manifest only, MISSING from folder', cls: 'missing' },
}

function formatMetadataValue(value: unknown): string {
  if (typeof value === 'string') return value
  return JSON.stringify(value, null, 1)
}

function ShaCell({ row }: { row: ToyFileRow }) {
  if (row.shaStatus === 'match') {
    return <span className="admin-user__status--indexed">✓ match</span>
  }
  if (row.shaStatus === 'mismatch') {
    return (
      <span className="admin-user__status--missing">
        ✗ mismatch
        <span className="toy-detail__sha">
          manifest {row.manifestSha}
          <br />
          actual&nbsp;&nbsp;&nbsp;{row.actualSha}
        </span>
      </span>
    )
  }
  if (row.shaStatus === 'pending') return <span>checking…</span>
  return <span className="admin-user__status--indexed">—</span>
}

type FirmwareScanState =
  | { kind: 'loading' }
  | { kind: 'ready'; builds: FirmwareBuild[] }
  | { kind: 'error'; message: string }

const BUILD_STATUS_LABELS = {
  current: { label: 'current', cls: 'indexed' },
  newer: { label: 'newer', cls: 'orphan' },
  undated: { label: 'undated', cls: 'missing' },
  older: { label: 'older', cls: '' },
} as const

// Lists every build of this toy's firmware project so admins can compare
// them, and lets the admin stage a new toy version from a build strictly
// newer than the one this version embeds (compared by metadata build_time)
// — downgrades are deliberately disabled. Staged versions are dormant:
// versions-index.json keeps pointing at the current latest.
function FirmwareUpdates({
  identityId,
  toyId,
  version,
  manifestName,
  firmwareMetadata,
}: {
  identityId: string
  toyId: string
  version: string | null
  manifestName: string | null
  firmwareMetadata: Record<string, unknown> | undefined
}) {
  const adminIo = useAdminIo()
  const [state, setState] = useState<FirmwareScanState>({ kind: 'loading' })
  const [upgrade, setUpgrade] = useState<{ gitHash: string; step: string } | null>(null)

  const packageName =
    firmwareMetadata && typeof firmwareMetadata.package_name === 'string'
      ? firmwareMetadata.package_name
      : null
  const currentBuildTime = firmwareMetadata?.build_time
  const currentGitHash =
    firmwareMetadata && typeof firmwareMetadata.git_hash === 'string'
      ? firmwareMetadata.git_hash
      : null

  useEffect(() => {
    if (!adminIo || !packageName) return
    let cancelled = false
    listFirmwareBuilds(adminIo, packageName)
      .then((builds) => {
        if (cancelled) return
        setState({ kind: 'ready', builds: sortBuildsNewestFirst(builds) })
      })
      .catch((err: unknown) => {
        if (cancelled) return
        console.error('[admin] listFirmwareBuilds failed', { packageName }, err)
        const message = err instanceof Error ? err.message : 'Firmware scan failed'
        setState({ kind: 'error', message })
      })
    return () => {
      cancelled = true
    }
  }, [adminIo, packageName])

  // A staged upgrade needs a source version folder and its manifest — and the
  // Featured folder is publish-only (versions are minted in the source toy
  // and copied over).
  const isFeaturedFolder = identityId === FEATURED_IDENTITY_ID
  const canUpgrade = version !== null && manifestName !== null && !isFeaturedFolder

  const baselineOk = parseableTime(currentBuildTime) !== null
  const newerCount =
    state.kind === 'ready'
      ? state.builds.filter((b) => isNewerBuild(b, currentBuildTime)).length
      : 0

  const handleUseFirmware = async (build: FirmwareBuild) => {
    if (!adminIo || !packageName || version === null || manifestName === null) return
    const newVersion = window.prompt(
      `Stage a new version of this toy with firmware build ${build.gitHash.slice(0, 12)}?\n\nNew version number:`,
      bumpMinor(version),
    )
    if (newVersion === null) return
    if (!isSemver(newVersion.trim())) {
      window.alert(`Version must look like MAJOR.MINOR.PATCH, got "${newVersion}"`)
      return
    }
    setUpgrade({ gitHash: build.gitHash, step: 'Starting…' })
    try {
      await upgradeToyFirmware(adminIo, identityId, toyId, {
        fromVersion: version,
        newVersion: newVersion.trim(),
        manifestName,
        target: { project: packageName, gitHash: build.gitHash },
        onStep: (step) => setUpgrade({ gitHash: build.gitHash, step }),
      })
      window.alert(
        `Staged ${newVersion.trim()} with firmware ${build.gitHash.slice(0, 12)}.\n\n` +
          `It is NOT live: versions-index.json still points at ${version}. ` +
          'Promote it by updating "latest" when ready.',
      )
    } catch (err) {
      console.error('[admin] upgradeToyFirmware failed', { toyId, build: build.gitHash }, err)
      const message = err instanceof Error ? err.message : 'Upgrade failed'
      window.alert(`Upgrade failed: ${message}`)
    } finally {
      setUpgrade(null)
    }
  }

  return (
    <>
      <h3>Firmware updates</h3>
      {!packageName && (
        <p className="toy-detail__status">
          No firmwareMetadata.package_name — cannot scan for firmware updates.
        </p>
      )}
      {packageName && state.kind === 'loading' && (
        <p className="toy-detail__status">Scanning firmware builds…</p>
      )}
      {packageName && state.kind === 'error' && (
        <p className="admin__error" role="alert">
          Firmware scan failed: {state.message}
        </p>
      )}
      {packageName && state.kind === 'ready' && state.builds.length === 0 && (
        <p className="toy-detail__status">
          No builds of {packageName} found in the firmware bucket.
        </p>
      )}
      {packageName && state.kind === 'ready' && state.builds.length > 0 && (
        <>
          {!baselineOk ? (
            <p className="admin__error" role="alert">
              This version&apos;s firmware has no parseable build_time — cannot tell which
              builds are newer, so installing is disabled.
            </p>
          ) : newerCount > 0 ? (
            <p className="admin__firmware-alert" role="alert">
              {newerCount} newer build{newerCount === 1 ? '' : 's'} of {packageName}{' '}
              available
              {typeof currentBuildTime === 'string' && ` (current built ${currentBuildTime})`}.
            </p>
          ) : (
            <p className="admin__count">
              Firmware is up to date ({packageName}
              {currentGitHash && ` @ ${currentGitHash.slice(0, 12)}`}
              {typeof currentBuildTime === 'string' && `, built ${currentBuildTime}`}).
            </p>
          )}
          {isFeaturedFolder && (
            <p className="toy-detail__status">
              Firmware updates are disabled in the Featured folder — update the source toy
              and copy the new version over.
            </p>
          )}
          <table className="admin-user__table">
            <thead>
              <tr>
                <th>Build</th>
                <th>Built</th>
                <th>Status</th>
                <th>Metadata</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {state.builds.map((build) => {
                const isCurrent = currentGitHash !== null && build.gitHash === currentGitHash
                const newer = isNewerBuild(build, currentBuildTime)
                const status = isCurrent
                  ? BUILD_STATUS_LABELS.current
                  : newer
                    ? BUILD_STATUS_LABELS.newer
                    : build.buildTime === null
                      ? BUILD_STATUS_LABELS.undated
                      : BUILD_STATUS_LABELS.older
                return (
                  <tr key={build.gitHash}>
                    <td className="admin-user__toy-id">{build.gitHash}</td>
                    <td>{build.buildTime ?? '—'}</td>
                    <td>
                      <span className={status.cls && `admin-user__status--${status.cls}`}>
                        {status.label}
                      </span>
                    </td>
                    <td>
                      <pre className="toy-detail__metadata-value">
                        {formatMetadataValue(build.metadata)}
                      </pre>
                    </td>
                    <td className="admin-user__row-actions">
                      {canUpgrade && (
                        <button
                          type="button"
                          onClick={() => void handleUseFirmware(build)}
                          disabled={upgrade !== null || !newer}
                          title={
                            newer
                              ? undefined
                              : isCurrent
                                ? 'This is the currently embedded build'
                                : baselineOk
                                  ? 'Not newer than the embedded firmware — installing older firmware is disabled'
                                  : 'Cannot compare build times — installing is disabled'
                          }
                        >
                          {upgrade?.gitHash === build.gitHash
                            ? `${upgrade.step}`
                            : 'Use this firmware'}
                        </button>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </>
      )}
    </>
  )
}

type VersionsListState =
  | { kind: 'loading' }
  | { kind: 'ready'; versions: ToyVersionSummary[] }
  | { kind: 'error'; message: string }

function versionFirmwareSummary(metadata: Record<string, unknown> | null): string | null {
  const fw = metadata?.firmwareMetadata
  if (!fw || typeof fw !== 'object' || Array.isArray(fw)) return null
  const f = fw as Record<string, unknown>
  const pkg = typeof f.package_name === 'string' ? f.package_name : null
  const hash = typeof f.git_hash === 'string' ? ` @ ${f.git_hash.slice(0, 12)}` : ''
  const built = typeof f.build_time === 'string' ? `, built ${f.build_time}` : ''
  return pkg ? `${pkg}${hash}${built}` : null
}

// The versions of a toy, newest first, linking into the per-version
// inspection view. Live = what versions-index.json currently serves;
// staged = a folder (e.g. from a firmware upgrade) the index doesn't
// point at yet.
function AdminToyVersions({
  identityId,
  toyId,
  refreshKey,
  onChanged,
}: {
  identityId: string
  toyId: string
  refreshKey: number
  onChanged: () => void
}) {
  const adminIo = useAdminIo()
  const [state, setState] = useState<VersionsListState>({ kind: 'loading' })
  const [busy, setBusy] = useState<{
    action: 'promote' | 'delete' | 'copy'
    version: string
  } | null>(null)
  // A newer firmware build than this toy's newest version embeds, surfaced
  // above the versions list. null = none available / not yet scanned.
  const [fwAlert, setFwAlert] = useState<{ count: number; packageName: string } | null>(null)

  useEffect(() => {
    if (!adminIo) return
    let cancelled = false
    setFwAlert(null)
    listToyVersionSummaries(adminIo, identityId, toyId)
      .then(async (versions) => {
        if (cancelled) return
        setState({ kind: 'ready', versions })
        // Scan for firmware newer than the newest version folder (sorted
        // newest-first). A scan failure must not take down the versions list —
        // the per-version page reports firmware errors in detail.
        if (versions.length === 0) return
        const { packageName, buildTime } = firmwareMetaOf(versions[0].metadata)
        if (!packageName) return
        try {
          const builds = await listFirmwareBuilds(adminIo, packageName)
          if (cancelled) return
          const count = filterNewerBuilds(builds, buildTime).length
          if (count > 0) setFwAlert({ count, packageName })
        } catch (err) {
          console.error('[admin] version-list firmware scan failed', { identityId, toyId }, err)
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return
        console.error('[admin] listToyVersionSummaries failed', { identityId, toyId }, err)
        const message = err instanceof Error ? err.message : 'Failed to list versions'
        setState({ kind: 'error', message })
      })
    return () => {
      cancelled = true
    }
  }, [adminIo, identityId, toyId, refreshKey])

  const base = `/admin/users/${encodeURIComponent(identityId)}/toys/${toyId}`

  if (state.kind === 'loading') {
    return <p className="toy-detail__status">Listing versions…</p>
  }
  if (state.kind === 'error') {
    return (
      <p className="admin__error" role="alert">
        Failed to list versions: {state.message}
      </p>
    )
  }
  if (state.versions.length === 0) {
    return (
      <p className="toy-detail__status">
        No version folders — this toy uses the legacy un-versioned layout.{' '}
        <Link to={`${base}/legacy`}>Inspect legacy layout</Link>
      </p>
    )
  }

  const currentLatest = state.versions.find((v) => v.isLatest)?.version

  const handleMakeLive = async (version: string) => {
    if (!adminIo) return
    if (
      !window.confirm(
        `Make ${version} the live version of this toy?\n\n` +
          `versions-index.json "latest" will change${currentLatest ? ` from ${currentLatest}` : ''} ` +
          `to ${version} and toy-index.json will be updated. ` +
          `New installs will serve ${version} immediately.`,
      )
    ) {
      return
    }
    setBusy({ action: 'promote', version })
    try {
      await promoteToyVersion(adminIo, identityId, toyId, version)
      if (identityId === FEATURED_IDENTITY_ID) {
        // The public Featured pages read the static /data/toy-index.json, so
        // a featured promote isn't publicly visible until that file is
        // re-pulled and the frontend redeployed.
        window.alert(
          `${version} is now live in the Featured folder.\n\n` +
            'Note: the public Featured list reads the deployed data/toy-index.json — ' +
            'pull the updated index from S3 and redeploy to publish this change.',
        )
      }
      // The hero section (title/version/Install link) comes from the
      // toy-index entry that just changed, not from this component —
      // onChanged bumps the parent's refresh nonce to reload both.
      onChanged()
    } catch (err) {
      console.error('[admin] promoteToyVersion failed', { identityId, toyId, version }, err)
      const message = err instanceof Error ? err.message : 'Promote failed'
      window.alert(`Promote failed: ${message}`)
    } finally {
      setBusy(null)
    }
  }

  const handleCopyToFeatured = async (version: string) => {
    if (!adminIo) return
    if (
      !window.confirm(
        `Copy version ${version} of this toy to Featured Apps?\n\n` +
          'It keeps its version number and lands dormant — promote it on the ' +
          'featured toy’s versions page to publish it. (The first featured ' +
          'version of a toy goes live immediately.)',
      )
    ) {
      return
    }
    setBusy({ action: 'copy', version })
    try {
      const { live } = await copyVersionToFeatured(adminIo, identityId, toyId, version)
      window.alert(
        live
          ? `Copied ${version} to Featured — it is the toy's first featured version and is now live there. ` +
              'Pull the updated data/toy-index.json and redeploy to publish it.'
          : `Copied ${version} to Featured as a staged version. Use "Make live" on the featured toy's versions page to publish it.`,
      )
    } catch (err) {
      console.error('[admin] copyVersionToFeatured failed', { identityId, toyId, version }, err)
      const message = err instanceof Error ? err.message : 'Copy failed'
      window.alert(`Copy failed: ${message}`)
    } finally {
      setBusy(null)
    }
  }

  const handleDelete = async (version: string) => {
    if (!adminIo) return
    if (
      !window.confirm(
        `Delete version ${version} of this toy?\n\n` +
          'This permanently removes all files in its folder and its ' +
          'versions-index.json entry. The live version is not affected.',
      )
    ) {
      return
    }
    setBusy({ action: 'delete', version })
    try {
      await deleteToyVersion(adminIo, identityId, toyId, version)
      onChanged()
    } catch (err) {
      console.error('[admin] deleteToyVersion failed', { identityId, toyId, version }, err)
      const message = err instanceof Error ? err.message : 'Delete failed'
      window.alert(`Delete failed: ${message}`)
    } finally {
      setBusy(null)
    }
  }

  return (
    <>
      {fwAlert && (
        <p className="admin__firmware-alert" role="alert">
          {fwAlert.count} newer firmware build{fwAlert.count === 1 ? '' : 's'} of{' '}
          {fwAlert.packageName} available. Open a version below to stage an update.
        </p>
      )}
      <p className="admin__count">
        {state.versions.length} version{state.versions.length === 1 ? '' : 's'}
      </p>
      <table className="admin-user__table">
        <thead>
          <tr>
            <th>Version</th>
            <th>Status</th>
            <th>Firmware</th>
            <th>Created</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {state.versions.map((v) => {
            const status = v.isLatest
              ? { label: 'live', cls: 'indexed' as const }
              : v.metadata
                ? { label: 'staged', cls: 'orphan' as const }
                : { label: 'incomplete', cls: 'missing' as const }
            const isBusy = (action: 'promote' | 'delete' | 'copy') =>
              busy?.action === action && busy.version === v.version
            return (
              <tr key={v.version}>
                <td>
                  <Link to={`${base}/${v.version}`}>{v.version}</Link>
                </td>
                <td className={`admin-user__status--${status.cls}`}>{status.label}</td>
                <td>{versionFirmwareSummary(v.metadata) ?? '—'}</td>
                <td>{v.createdAt ?? '—'}</td>
                <td className="admin-user__row-actions">
                  {!v.isLatest && v.metadata && (
                    <button
                      type="button"
                      onClick={() => void handleMakeLive(v.version)}
                      disabled={busy !== null}
                    >
                      {isBusy('promote') ? 'Promoting…' : 'Make live'}
                    </button>
                  )}
                  {identityId !== FEATURED_IDENTITY_ID && v.metadata && (
                    <button
                      type="button"
                      onClick={() => void handleCopyToFeatured(v.version)}
                      disabled={busy !== null}
                    >
                      {isBusy('copy') ? 'Copying…' : 'Copy to Featured'}
                    </button>
                  )}
                  {!v.isLatest && (
                    <button
                      type="button"
                      onClick={() => void handleDelete(v.version)}
                      disabled={busy !== null}
                    >
                      {isBusy('delete') ? 'Deleting…' : 'Delete'}
                    </button>
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </>
  )
}

// Extra admin-only panel: the raw metadata.json plus a cross-check of the
// files actually in the toy's S3 folder against the OMF manifest's entries,
// including SHA-256 verification of the file bytes. version pins which
// folder is inspected (null = legacy toy root).
function AdminToyInspection({
  identityId,
  toyId,
  version,
}: {
  identityId: string
  toyId: string
  version: string | null
}) {
  const adminIo = useAdminIo()
  const [state, setState] = useState<InspectionState>({ kind: 'loading' })

  useEffect(() => {
    if (!adminIo) return
    let cancelled = false
    inspectToy(adminIo, identityId, toyId, version)
      .then((inspection) => {
        if (cancelled) return
        setState({ kind: 'ready', inspection })
        // Checksum verification downloads every file, so it runs after the
        // cheap presence listing is already on screen, filling rows in as
        // they finish.
        return verifyToyShas(adminIo, identityId, toyId, inspection, (row) => {
          if (cancelled) return
          setState((prev) => {
            if (prev.kind !== 'ready') return prev
            return {
              kind: 'ready',
              inspection: {
                ...prev.inspection,
                files: prev.inspection.files.map((f) => (f.name === row.name ? row : f)),
              },
            }
          })
        })
      })
      .catch((err: unknown) => {
        if (cancelled) return
        console.error('[admin] inspectToy failed', { identityId, toyId }, err)
        const message = err instanceof Error ? err.message : 'Inspection failed'
        setState({ kind: 'error', message })
      })
    return () => {
      cancelled = true
    }
  }, [adminIo, identityId, toyId, version])

  if (state.kind === 'loading') {
    return <p className="toy-detail__status">Inspecting toy folder…</p>
  }
  if (state.kind === 'error') {
    return (
      <p className="admin__error" role="alert">
        Inspection failed: {state.message}
      </p>
    )
  }

  const { inspection } = state
  const fwMeta = inspection.metadata?.firmwareMetadata
  const firmwareMetadata =
    fwMeta && typeof fwMeta === 'object' && !Array.isArray(fwMeta)
      ? (fwMeta as Record<string, unknown>)
      : undefined
  return (
    <>
      <p className="admin__count">
        {inspection.version ? `Version ${inspection.version}` : 'Legacy (un-versioned) layout'}
        {inspection.appName && ` · app "${inspection.appName}"`}
        {inspection.manifestName && ` · ${inspection.manifestName}`}
      </p>

      {inspection.problems.map((p) => (
        <p key={p} className="admin__error" role="alert">
          {p}
        </p>
      ))}

      <h3>metadata.json</h3>
      {inspection.metadata ? (
        <table className="admin-user__table">
          <tbody>
            {Object.entries(inspection.metadata).map(([key, value]) => (
              <tr key={key}>
                <th scope="row">{key}</th>
                <td>
                  <pre className="toy-detail__metadata-value">{formatMetadataValue(value)}</pre>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="toy-detail__status">No readable metadata.json.</p>
      )}

      <FirmwareUpdates
        identityId={identityId}
        toyId={toyId}
        version={inspection.version}
        manifestName={inspection.manifestName}
        firmwareMetadata={firmwareMetadata}
      />

      <h3>Files: folder vs manifest</h3>
      {inspection.files.length === 0 ? (
        <p className="toy-detail__status">No files found.</p>
      ) : (
        <table className="admin-user__table">
          <thead>
            <tr>
              <th>File</th>
              <th>Size</th>
              <th>Location</th>
              <th>SHA-256</th>
            </tr>
          </thead>
          <tbody>
            {inspection.files.map((row) => {
              const presence = PRESENCE_LABELS[row.presence]
              return (
                <tr key={row.name}>
                  <td className="admin-user__toy-id">{row.name}</td>
                  <td>{row.size !== undefined ? row.size.toLocaleString() : '—'}</td>
                  <td className={`admin-user__status--${presence.cls}`}>{presence.label}</td>
                  <td>
                    <ShaCell row={row} />
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}
    </>
  )
}

// source scopes which copy of the toy the public routes resolve:
// 'featured' (/toys/featured/:id) reads only the static featured index,
// 'mine' (/toys/my/:id) reads only the signed-in user's toy-index, and
// undefined (legacy /toys/:id) keeps the historical featured-then-own lookup.
export default function ToyDetail({ source }: { source?: 'featured' | 'mine' }) {
  const { id, identityId: rawAdminIdentityId, version: rawVersion } = useParams<{
    id: string
    identityId: string
    version: string
  }>()
  // identityId is present only on the admin routes
  // (/admin/users/:identityId/toys/:id[/:version]), which is what switches
  // this page into admin mode. Without :version the admin section lists the
  // toy's versions; with it, it inspects that one version ('legacy' = the
  // un-versioned toy root).
  const adminIdentityId = rawAdminIdentityId ? decodeURIComponent(rawAdminIdentityId) : null
  const io = useCurrentIo()
  const adminIo = useAdminIo()
  const [state, setState] = useState<LoadState>({ kind: 'loading' })
  // Bumped after admin mutations (promote/delete) so the hero and the
  // versions list refresh in place — a full reload would hit the hosting's
  // missing SPA-route handling.
  const [adminRefresh, setAdminRefresh] = useState(0)

  // Normal mode. The scoped routes resolve exactly one source — the same toy
  // id can exist both as the user's own copy and as a physically separate
  // featured copy, so which one to show is the URL's decision. The legacy
  // un-scoped route keeps the historical featured-then-own lookup.
  useEffect(() => {
    if (adminIdentityId) return
    const controller = new AbortController()
    let cancelled = false

    const tryUserIndex = async (): Promise<FeaturedToy | null> => {
      if (!io) return null
      const index = await loadToyIndex(io)
      for (const entry of index) {
        const toy = entryToToy(entry)
        if (toy && toy.id === id) return toy
      }
      return null
    }

    const resolve = async (): Promise<FeaturedToy | null> => {
      if (source === 'mine') return tryUserIndex()
      const featured = (await fetchFeaturedToys({ signal: controller.signal })).find(
        (t) => t.id === id,
      )
      if (source === 'featured') return featured ?? null
      return featured ?? (await tryUserIndex())
    }

    resolve()
      .then((toy) => {
        if (cancelled) return
        setState(toy ? { kind: 'ready', toy } : { kind: 'not-found' })
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted || cancelled) return
        const message = err instanceof Error ? err.message : 'Failed to load'
        setState({ kind: 'error', message })
      })

    return () => {
      controller.abort()
      cancelled = true
    }
  }, [id, io, adminIdentityId, source])

  // Admin mode: the toy belongs to the inspected user. On the versions-list
  // page the hero shows the live version (that user's toy-index entry); on a
  // per-version page it shows THAT version's own metadata, so the title,
  // version line, and Install link all belong to the version being viewed —
  // including staged ones the index doesn't serve yet.
  useEffect(() => {
    if (!adminIdentityId || !adminIo || !id) return
    let cancelled = false

    const resolve = async (): Promise<FeaturedToy | null> => {
      if (rawVersion !== undefined) {
        const metadata = await loadVersionMetadata(
          adminIo,
          adminIdentityId,
          id,
          rawVersion === 'legacy' ? null : rawVersion,
        )
        return metadata ? entryToToy(metadata) : null
      }
      const index = await loadToyIndex(adminIo, adminIdentityId)
      for (const entry of index) {
        const toy = entryToToy(entry)
        if (toy && toy.id === id) return toy
      }
      return null
    }

    resolve()
      .then((toy) => {
        if (cancelled) return
        setState(toy ? { kind: 'ready', toy } : { kind: 'not-found' })
      })
      .catch((err: unknown) => {
        if (cancelled) return
        const message = err instanceof Error ? err.message : 'Failed to load'
        setState({ kind: 'error', message })
      })
    return () => {
      cancelled = true
    }
  }, [adminIdentityId, adminIo, id, rawVersion, adminRefresh])

  return (
    <section className="toy-detail">
      {adminIdentityId && rawVersion !== undefined ? (
        <Link
          to={`/admin/users/${encodeURIComponent(adminIdentityId)}/toys/${id}`}
          className="detail-back"
        >
          &larr; All versions
        </Link>
      ) : adminIdentityId ? (
        <Link
          to={`/admin/users/${encodeURIComponent(adminIdentityId)}`}
          className="detail-back"
        >
          &larr; Back to user
        </Link>
      ) : (
        <Link to="/" className="detail-back">
          &larr; Back to Toys
        </Link>
      )}

      {state.kind === 'loading' && <p className="toy-detail__status">Loading…</p>}

      {state.kind === 'error' && (
        <p className="toy-detail__status" role="alert">
          Couldn't load toy: {state.message}
        </p>
      )}

      {state.kind === 'not-found' && (
        <p className="toy-detail__status">
          {adminIdentityId && rawVersion !== undefined
            ? 'No readable metadata.json for this version.'
            : adminIdentityId
              ? 'Toy not in this user’s toy-index.json.'
              : 'Toy not found.'}
        </p>
      )}

      {state.kind === 'ready' && (
        <>
          {state.toy.thumbnailUrl ? (
            <img
              src={state.toy.thumbnailUrl}
              alt={state.toy.title}
              className="toy-detail__hero"
            />
          ) : (
            <div className="toy-detail__placeholder" aria-hidden="true">
              {state.toy.title.charAt(0)}
            </div>
          )}

          <h1 className="detail-title">{state.toy.title}</h1>

          {state.toy.creatorName && (
            <p className="toy-detail__creator">by {state.toy.creatorName}</p>
          )}

          {state.toy.version && (
            <p className="toy-detail__version">Version {state.toy.version}</p>
          )}

          {state.toy.description && (
            <p className="toy-detail__description">{state.toy.description}</p>
          )}

          {state.toy.manifestUrl && (
            <a
              href={`https://connect.oroblanco.app/installer/index.html?firmwareUrl=${encodeURIComponent(state.toy.manifestUrl)}`}
              className="toy-detail__install"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                Sentry.metrics.count('toy.install_click', 1, {
                  attributes: {
                    toyId: state.toy.id,
                    toyTitle: state.toy.title,
                  },
                })
              }}
            >
              Install
            </a>
          )}
        </>
      )}

      {adminIdentityId && id && state.kind !== 'loading' && (
        <div className="toy-detail__admin">
          <h2>Admin details</h2>
          <p className="admin-user__id">
            {adminIdentityId}/{id}
            {rawVersion !== undefined && `/${rawVersion}`}
          </p>
          {rawVersion === undefined ? (
            <AdminToyVersions
              identityId={adminIdentityId}
              toyId={id}
              refreshKey={adminRefresh}
              onChanged={() => setAdminRefresh((n) => n + 1)}
            />
          ) : (
            <AdminToyInspection
              identityId={adminIdentityId}
              toyId={id}
              version={rawVersion === 'legacy' ? null : rawVersion}
            />
          )}
        </div>
      )}
    </section>
  )
}
